import {
  AutopilotTaskDto,
  TaskCategoryStage,
  TaskCategoryTaskType,
  TaskCategoryWorkflowType,
  TaskStepDto,
} from '@urbanx/agx.activities.client';
import { CampaignDetailModelState } from '@urbanx/agx.campaigns.client';

export interface AgreementComplianceStep {
  task: ResolvedTask;
  step: TaskStepDto;
  displayName?: string;
}

type ResolvedTask = AutopilotTaskDto & { id: string };

const hasTaskId = (task: AutopilotTaskDto | undefined): task is ResolvedTask => Boolean(task?.id);

export const getInteractiveAgreementComplianceTasks = (tasks: AutopilotTaskDto[]) =>
  tasks.filter(
    (task) =>
      !task.isLocked &&
      task.category?.workflowType === TaskCategoryWorkflowType.Campaign &&
      task.category?.stage === TaskCategoryStage.AgreementCompliance
  );

export interface AgreementComplianceTaskLayout {
  pageTasks: AutopilotTaskDto[];
  agreementDetailsSteps: AgreementComplianceStep[];
  commissionStep: AgreementComplianceStep;
}

const taskByType = (tasks: AutopilotTaskDto[], taskType: TaskCategoryTaskType) => {
  const task = tasks.find((candidate) => candidate.category?.taskType === taskType);

  if (!hasTaskId(task)) {
    throw new Error(`Agreement Compliance task ${taskType} is missing`);
  }

  return task;
};

const stepByOrder = (
  task: ResolvedTask,
  order: number,
  displayName?: string
): AgreementComplianceStep => {
  const step = task.taskSteps?.find((candidate) => candidate.order === order);

  if (!step) {
    throw new Error(`Agreement Compliance step ${task.category?.taskType}:${order} is missing`);
  }

  return { task, step, displayName };
};

const resolveQldLayout = (tasks: AutopilotTaskDto[]): AgreementComplianceTaskLayout => {
  const detailsTask = taskByType(tasks, TaskCategoryTaskType.AgreementDetails);
  const agreementTypeTask = taskByType(tasks, TaskCategoryTaskType.AgreementComplianceType);
  const commissionTask = taskByType(tasks, TaskCategoryTaskType.AgreementCommission);
  const baseSteps = [1, 2, 3, 4].map((order) => stepByOrder(detailsTask, order));
  const hasCurrentLayout = detailsTask.taskSteps?.length === 6;

  const signatureSteps = hasCurrentLayout
    ? [5, 6].map((order) => stepByOrder(detailsTask, order))
    : [stepByOrder(agreementTypeTask, 4), stepByOrder(commissionTask, 2)];

  return {
    pageTasks: [detailsTask, agreementTypeTask, commissionTask],
    agreementDetailsSteps: [...baseSteps, ...signatureSteps],
    commissionStep: stepByOrder(commissionTask, 1),
  };
};

export const resolveAgreementComplianceTaskLayout = (
  tasks: AutopilotTaskDto[],
  state: CampaignDetailModelState | undefined
) => {
  if (state !== CampaignDetailModelState.QLD) {
    throw new Error(`Agreement Compliance layout is not supported for ${state}`);
  }

  return resolveQldLayout(tasks);
};

export const canAttemptAgreementComplianceCompletion = (
  tasks: AutopilotTaskDto[],
  state: CampaignDetailModelState | undefined
) => {
  const { commissionStep } = resolveAgreementComplianceTaskLayout(tasks, state);

  return tasks.every(
    (task) =>
      (task.taskSteps?.length ?? 0) > 0 &&
      (task.taskSteps ?? []).every(
        (step) =>
          (task === commissionStep.task && step.order === commissionStep.step.order) ||
          step.isCompleted
      )
  );
};

export const createAgreementComplianceTaskModalFlow = (state: CampaignDetailModelState) => ({
  resolvePageTasks: (tasks: AutopilotTaskDto[]) =>
    resolveAgreementComplianceTaskLayout(tasks, state).pageTasks,
  resolvePersistenceTasks: (tasks: AutopilotTaskDto[]) => tasks,
  canComplete: (tasks: AutopilotTaskDto[]) => canAttemptAgreementComplianceCompletion(tasks, state),
});
